import axios, { type AxiosInstance } from 'axios';
import { toast } from 'sonner';

import { connectivity, type HubUnreachableKind } from '@/lib/connectivity';
import { queryClient } from '@/lib/query-client';
import { ACCESS_TOKEN_STORAGE_KEY, useAuthStore } from '@/store/auth';

/**
 * Shared axios instance for the Hub REST API (Contract C).
 *
 * `baseURL` is `/api/v1` so every call lands on the Vite dev proxy in dev and
 * the nginx reverse proxy in production. Adapters belong in `src/api/`; this
 * module just exposes the underlying client and the interceptor wiring.
 *
 * INTERCEPTORS (M5-T2):
 *   - Request:  read the JWT from localStorage and attach as `Authorization`.
 *               localStorage (not the Zustand store) because the interceptor
 *               runs outside React's render lifecycle; localStorage is the
 *               single source of truth that survives cross-tab edits too.
 *   - Response: on 401, silently clear the session and bounce to /login.
 *               Quote (CTO guardrail): "对 401 报错有极其优雅的处理（静默清
 *               Token,柔和跳转回登录页,不要弹出乱七八糟的报错对话框)".
 *               One gentle sonner toast at most; never an alert dialog or a
 *               console.error.
 *   - Response (2026-09, T1-Fa): every outcome also feeds the connectivity
 *               store (`lib/connectivity.ts`) — see "Hub reachability" below.
 *               It never touches the session; the 401 path is unchanged.
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
});

apiClient.interceptors.request.use((config) => {
  // Read directly from localStorage. The Zustand store mirrors it but the
  // interceptor isn't a React hook — we don't want a stale closure here, and
  // localStorage is the single source of truth that survives reloads.
  const token = window.localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Navigation helper for the 401 path.
 *
 * Chosen (a): a setter the App mounts at boot to register a react-router
 * `navigate` function. Picked over (b) `window.location.assign` so the SPA
 * shell never reloads — no white flash, no re-mount of the QueryClient, no
 * lost dev-time React Query devtools state. Falls back to `window.location`
 * if the App hasn't registered yet (the interceptor would only fire that
 * early in a degenerate code path, but the fallback keeps the user from
 * getting stuck on a half-rendered page).
 */
let _navigate: ((path: string) => void) | null = null;

export function registerApiNavigator(navigate: (path: string) => void): void {
  _navigate = navigate;
}

// ---------------------------------------------------------------------------
// Hub reachability (2026-09 disconnect early warning — spec
// docs/specs/2026-09-features/01-disconnect-early-warning.md §3.6; bug 5)
// ---------------------------------------------------------------------------

/**
 * The marker nginx's edge error page carries in its JSON BODY
 * (`frontend/nginx.conf`, `location @hub_unreachable`, answered as a 503 for
 * every 502/503/504 nginx itself generates while proxying `/api/`). It lives
 * in the body, not a header, on purpose: an `add_header` in that location
 * would cancel the server-level security headers (nginx inheritance).
 */
export const HUB_UNREACHABLE_EDGE_MARKER = 'hub_unreachable';

/**
 * Why a failed request means the hub could NOT be reached — or `null` when
 * the hub (or nobody at all) answered. One branch per
 * {@link HubUnreachableKind}, evaluated on the error exactly as axios hands
 * it to a `catch` (body already JSON-parsed by `transformResponse`):
 *
 *   - `'network'` — no HTTP response at all: DNS / TCP / TLS failure, the
 *     browser offline, a timeout or a browser-side abort. A deliberate
 *     cancellation (`CanceledError`) is NOT an outage and returns `null`.
 *   - `'edge'`    — a JSON body with `edge === "hub_unreachable"`: nginx's
 *     edge page (see {@link HUB_UNREACHABLE_EDGE_MARKER}).
 *   - `'proxy'`   — a 502 / 504 whose body is NOT JSON: some proxy's own
 *     error page, e.g. the host TLS nginx's HTML 502 when the console's
 *     frontend container itself is down.
 *
 * Everything else returns `null`, in particular the hub's OWN errors: its
 * JSON 502 (Sheets) / 503 (gateway RPC) and its `text/plain` 500 all mean the
 * hub is up and answering. Non-axios values (a zod parse failure after a
 * 200, a thrown TypeError) are never classified. Note the Vite 5 dev proxy
 * answers a hub it cannot reach with an EMPTY `text/plain` 500, not a 502,
 * so in `npm run dev` an outage is not classified here (the auth boundary
 * still keeps the session: it treats any 5xx as an outage, see
 * {@link isHubOutageError}).
 */
export function hubUnreachableKind(error: unknown): HubUnreachableKind | null {
  // Cancel check first: `CanceledError` IS an AxiosError without a response.
  if (axios.isCancel(error) || !axios.isAxiosError(error)) return null;
  const { response } = error;
  if (response === undefined) return 'network';
  const body: unknown = response.data;
  if (typeof body === 'object' && body !== null) {
    // A parsed JSON body: only the edge marker makes it "unreachable".
    return (body as { edge?: unknown }).edge === HUB_UNREACHABLE_EDGE_MARKER ? 'edge' : null;
  }
  return response.status === 502 || response.status === 504 ? 'proxy' : null;
}

/** Spec 01 §3.6 item 2: did this request fail because the hub was unreachable? */
export function isHubUnreachableError(error: unknown): boolean {
  return hubUnreachableKind(error) !== null;
}

/**
 * Hub unreachable OR the hub answered with a 5xx — "the hub could not serve
 * this request right now", as opposed to a verdict on the request itself.
 * The auth boundary's criterion (spec 01 §3.6 items 4–5): `/auth/me` failing
 * this way is retried with backoff and keeps the session; a 401 or any other
 * 4xx is not an outage.
 */
export function isHubOutageError(error: unknown): boolean {
  if (isHubUnreachableError(error)) return true;
  return axios.isAxiosError(error) && (error.response?.status ?? 0) >= 500;
}

/**
 * Tracks whether a 401 has already been handled this tick — guards against the
 * (rare) scenario where multiple in-flight requests all return 401 at once and
 * each would otherwise fire a toast + a navigation.
 */
let _handling401 = false;

apiClient.interceptors.response.use(
  (response) => {
    // Any successful response came from the hub. A no-op while already
    // reachable (the store returns the same state), so this is free.
    connectivity.markHubReachable();
    return response;
  },
  (error) => {
    // Connectivity first — it never touches the session. An unreachable
    // branch marks the outage; any other HTTP response (a 401 included, and
    // the hub's own 5xx) proves the hub answered. A cancelled request, or a
    // non-axios error, says nothing either way.
    const unreachable = hubUnreachableKind(error);
    if (unreachable !== null) {
      connectivity.markHubUnreachable(unreachable);
    } else if (axios.isAxiosError(error) && error.response !== undefined) {
      connectivity.markHubReachable();
    }

    const status = error?.response?.status;
    if (status === 401) {
      // Already on /login? The 401 is the *expected* result of /auth/me being
      // called by the route boundary while the user is signed out (or while
      // verifying a stale token). Do not toast, do not navigate — it would
      // either loop the message or yank focus off the login form.
      const onLoginPage =
        typeof window !== 'undefined' && window.location.pathname === '/login';

      if (!_handling401 && !onLoginPage) {
        _handling401 = true;
        try {
          useAuthStore.getState().clear();
          // Cancel any auth-namespaced queries in flight so the next render
          // doesn't refire /auth/me right after we've decided to redirect.
          void queryClient.cancelQueries({ queryKey: ['auth'] });
          queryClient.removeQueries({ queryKey: ['auth'] });
          toast('Session expired — please sign in again');
          if (_navigate) {
            _navigate('/login?reason=expired');
          } else {
            window.location.assign('/login?reason=expired');
          }
        } finally {
          // Release on the next tick so the burst of simultaneous 401s
          // coalesces into one toast / one navigation, but a *later* 401 from
          // a fresh sign-out cycle is handled normally.
          window.setTimeout(() => {
            _handling401 = false;
          }, 0);
        }
      } else if (onLoginPage) {
        // 401 from /login itself or from /auth/me while sitting on /login is
        // not a user-visible problem. The LoginPage handles its own inline
        // error; the boundary will silently render Navigate to /login (a
        // no-op since we're already here). Still clear stored state so a
        // stale token doesn't linger.
        useAuthStore.getState().clear();
      }
    }
    return Promise.reject(error);
  },
);
